import { api } from "./apiSlice";
import { unwrapApiResponse } from "../utils/apiUtils";
import { PaginatedList, PaginationParams } from "../types/pagination";
import { VehicleAssignmentDto } from "./vehicleAssignmentsApi";

// DTOs matching backend
export interface VehicleWithAccountStatusDto {
  id: number;
  name: string;
  hasAccount: boolean;
  userId: number | null;
  username: string | null;
  generatedUsername: string;
}

export interface TruckDriverAccountDto {
  userId: number;
  vehicleId: number;
  username: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  createdAt: string;
  vehicleName: string;
}

export interface CreateTruckDriverAccountDto {
  vehicleId: number;
  username: string;
  password: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface UpdateTruckDriverAccountDto {
  username?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export const truckAccountsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get all vehicles with account status
    getVehiclesWithAccountStatus: builder.query<VehicleWithAccountStatusDto[], void>({
      query: () => "truck-driver-accounts/vehicles",
      providesTags: ["TruckAccounts"],
      transformResponse: unwrapApiResponse,
    }),

    // Get paginated vehicles with account status
    getVehiclesWithAccountStatusPaginated: builder.query<PaginatedList<VehicleWithAccountStatusDto>, PaginationParams>({
      query: (params) => ({
        url: "truck-driver-accounts/vehicles",
        params: { ...params, paginated: true },
      }),
      providesTags: ["TruckAccounts"],
      transformResponse: unwrapApiResponse,
    }),

    // Create truck driver account
    createTruckDriverAccount: builder.mutation<TruckDriverAccountDto, CreateTruckDriverAccountDto>({
      query: (account) => ({
        url: "truck-driver-accounts",
        method: "POST",
        body: account,
      }),
      invalidatesTags: ["TruckAccounts"],
      transformResponse: unwrapApiResponse,
    }),

    // Get account by vehicle ID
    getAccountByVehicleId: builder.query<TruckDriverAccountDto, number>({
      query: (vehicleId) => `truck-driver-accounts/${vehicleId}`,
      providesTags: (result, error, vehicleId) => [{ type: "TruckAccounts", id: vehicleId }],
      transformResponse: unwrapApiResponse,
    }),

    // Update truck driver account
    updateTruckDriverAccount: builder.mutation<TruckDriverAccountDto, { vehicleId: number; account: UpdateTruckDriverAccountDto }>({
      query: ({ vehicleId, account }) => ({
        url: `truck-driver-accounts/${vehicleId}`,
        method: "PUT",
        body: account,
      }),
      invalidatesTags: (result, error, { vehicleId }) => [
        "TruckAccounts",
        { type: "TruckAccounts", id: vehicleId },
      ],
      transformResponse: unwrapApiResponse,
    }),

    // Delete truck driver account
    deleteTruckDriverAccount: builder.mutation<boolean, number>({
      query: (vehicleId) => ({
        url: `truck-driver-accounts/${vehicleId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["TruckAccounts"],
      transformResponse: unwrapApiResponse,
    }),

    // Generate username from vehicle
    generateUsernameFromVehicle: builder.query<string, number>({
      query: (vehicleId) => `truck-driver-accounts/${vehicleId}/generate-username`,
      transformResponse: unwrapApiResponse,
    }),

    // Check username availability
    checkUsernameAvailability: builder.query<boolean, string>({
      query: (username) => `truck-driver-accounts/check-username?username=${encodeURIComponent(username)}`,
      transformResponse: unwrapApiResponse,
    }),

    // Get current truck driver account (based on logged in user's token)
    getCurrentTruckDriverAccount: builder.query<TruckDriverAccountDto, void>({
      query: () => "truck-driver-accounts/",
      providesTags: ["TruckAccounts"],
      transformResponse: unwrapApiResponse,
    }),

    // Get vehicle assignments for current driver (based on logged in user's token)
    getVehicleAssignmentsOfDriver: builder.query<VehicleAssignmentDto[], void>({
      query: () => "vehicleassignments/profile",
      providesTags: ["VehicleAssignments"],
      transformResponse: unwrapApiResponse,
    }),
  }),
});

export const {
  useGetVehiclesWithAccountStatusQuery,
  useGetVehiclesWithAccountStatusPaginatedQuery,
  useCreateTruckDriverAccountMutation,
  useGetAccountByVehicleIdQuery,
  useUpdateTruckDriverAccountMutation,
  useDeleteTruckDriverAccountMutation,
  useGenerateUsernameFromVehicleQuery,
  useCheckUsernameAvailabilityQuery,
  useGetCurrentTruckDriverAccountQuery,
  useGetVehicleAssignmentsOfDriverQuery,
} = truckAccountsApi;
